import React, { useMemo } from "react";
import { EditContext } from "../context/EditContext";
import { useEditUI } from "../context/EditUIPreferencesContext";
import { EditTabs } from "../components/EditTabs";
import { EditTabsWithFolders } from "../components/EditTabsWithFolders";
import { EditGroup } from "../components/EditGroup";
import { EditField } from "../components/EditField";
import { useDefaultsEdit } from "./DefaultsEditContext";
import { typography, borders, surfaces, textColors } from "../../design-system/tokens";

export default function DefaultsEditPanel() {
  const { groupViewMode } = useEditUI();
  const {
    websiteId,
    selectedObject,
    editMetadata,
    entryLoading,
    saveStatus,
    error,
    mode,
    selectedInternal,
    closeInternalElement,
    updateField,
    updateMultipleFields,
    openInternalElement,
  } = useDefaultsEdit();

  const effectiveElement = useMemo(() => {
    if (!selectedObject) return null;
    // For tab persistence in EditTabs/EditTabsWithFolders, elementType is derived from EditContext.selectedElement.type.
    return selectedObject;
  }, [selectedObject]);

  const headerTitle = useMemo(() => {
    if (!effectiveElement?.type) return "Defaults";
    const label = String(effectiveElement.type).replace(/_/g, " ");
    return `Edit ${label.charAt(0).toUpperCase() + label.slice(1)}`;
  }, [effectiveElement]);

  const showBack = mode === "internal" && selectedInternal;

  const handleChange = (field, value) => updateField(field, value);

  if (entryLoading) {
    return <div className={`${typography.body} ${textColors.muted}`}>Loading…</div>;
  }

  if (!effectiveElement) {
    return (
      <div className={`${typography.body} ${textColors.muted}`}>
        Select a defaults entry to start editing.
      </div>
    );
  }

  // If we have a selected element but metadata hasn't resolved yet, show a dedicated loading state.
  // This avoids the confusing "Select…" placeholder when selection exists.
  if (!editMetadata) {
    return (
      <div className={`${typography.body} ${textColors.muted}`}>
        {error ? (
          <div className="text-red-600 dark:text-red-400">{error}</div>
        ) : (
          <div>Preparing fields…</div>
        )}
      </div>
    );
  }

  // For accordion mode, we mirror EditPanel's logic of extracting main_fields and grouped fields
  const mainFields = Object.entries(editMetadata.main_fields || {}).map(([field, metadata]) => ({
    field,
    metadata,
  }));

  const groupedFields = Object.entries(editMetadata).reduce((groups, [groupName, groupData]) => {
    if (groupName === "main_fields") return groups;
    const fields = Object.entries(groupData.fields || {}).map(([field, metadata]) => ({ field, metadata }));
    groups[groupName] = { groupInfo: groupData.group_info, fields };
    return groups;
  }, {});

  const header = (
    <div className={`flex items-center justify-between gap-3 pb-3 mb-4 border-b ${borders.default}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {showBack && (
            <button
              onClick={closeInternalElement}
              className={`px-2 py-1 rounded ${surfaces.secondary} ${textColors.primary} ${typography.bodySmall}`}
              title="Back"
            >
              ← Back
            </button>
          )}
          <div className={`${typography.h4} ${textColors.primary} truncate`}>{headerTitle}</div>
        </div>
        <div className={`${typography.bodySmall} ${textColors.tertiary} mt-1`}>
          {saveStatus === "saving" ? "Saving…" : saveStatus === "error" ? "Save failed" : "Saved"}
        </div>
      </div>

      {error && (
        <div className={`max-w-md text-right ${typography.bodySmall} text-red-600 dark:text-red-400`}>
          {error}
        </div>
      )}
    </div>
  );

  // Wrap children in a *local* EditContext.Provider so shared editor components that call useEdit()
  // (EditTabs/EditField) work without touching the live editor selection.
  const editContextValue = {
    websiteId,
    selectedElement: effectiveElement,
    selectElement: () => {},
    saveStatus,
    updateMultipleFields,
  };

  return (
    <EditContext.Provider value={editContextValue}>
      {header}

      {groupViewMode === "tabs" && (
        <EditTabs
          editMetadata={editMetadata}
          values={effectiveElement}
          onChange={handleChange}
          onEditInternalItem={openInternalElement}
          onDeleteInternalItem={null}
        />
      )}

      {groupViewMode === "folders" && (
        <EditTabsWithFolders
          editMetadata={editMetadata}
          values={effectiveElement}
          onChange={handleChange}
          onEditInternalItem={openInternalElement}
          onDeleteInternalItem={null}
        />
      )}

      {groupViewMode !== "tabs" && groupViewMode !== "folders" && (
        <div className="space-y-6">
          {/* Accordion mode: main fields first */}
          {mainFields.length > 0 && (
            <div className="space-y-4">
              {mainFields.map(({ field, metadata }) => (
                <div key={field} className={`mb-2 pb-2 border-b ${borders.light} last:border-b-0`}>
                  <EditField
                    field={field}
                    value={effectiveElement[field]}
                    metadata={metadata}
                    allValues={effectiveElement}
                    onChange={handleChange}
                  />
                </div>
              ))}
            </div>
          )}

          {Object.entries(groupedFields).map(([groupName, groupData]) => (
            <EditGroup
              key={groupName}
              groupName={groupName}
              groupInfo={groupData.groupInfo}
              fields={groupData.fields}
              values={effectiveElement}
              onChange={handleChange}
            />
          ))}
        </div>
      )}
    </EditContext.Provider>
  );
}

